import type { SupabaseClient } from "@supabase/supabase-js";

const MAX_PENDING_AVATAR_BYTES = 1024 * 1024;

export function withoutPendingAvatar(
  signupData: Record<string, unknown>,
): Record<string, unknown> {
  const { photoDataUrl: _photoDataUrl, ...sanitisedSignupData } = signupData;
  return sanitisedSignupData;
}

export async function uploadPendingSignupAvatar(input: {
  supabase: SupabaseClient;
  userId: string;
  value: unknown;
}): Promise<{ uploaded: boolean; error: string | null }> {
  if (typeof input.value !== "string") {
    return { uploaded: false, error: null };
  }
  const match = input.value.match(
    /^data:(image\/(?:jpeg|png|webp));base64,([A-Za-z0-9+/=]+)$/,
  );
  if (!match) return { uploaded: false, error: "Invalid pending avatar" };

  const contentType = match[1];
  const binary = atob(match[2]);
  if (
    binary.length === 0 ||
    binary.length > MAX_PENDING_AVATAR_BYTES
  ) {
    return {
      uploaded: false,
      error: "Pending avatar is outside the size limit",
    };
  }

  const bytes = Uint8Array.from(binary, (character) =>
    character.charCodeAt(0),
  );
  const extension =
    contentType === "image/png"
      ? "png"
      : contentType === "image/webp"
        ? "webp"
        : "jpg";
  const path = `${input.userId}/avatar.${extension}`;
  const { error: uploadError } = await input.supabase.storage
    .from("avatars")
    .upload(path, new Blob([bytes], { type: contentType }), {
      contentType,
      upsert: true,
    });
  if (uploadError) {
    return { uploaded: false, error: uploadError.message };
  }

  const { data: publicData } = input.supabase.storage
    .from("avatars")
    .getPublicUrl(path);
  const { error: updateError } = await input.supabase
    .from("profiles")
    .update({ photo_url: publicData.publicUrl })
    .eq("user_id", input.userId);
  return {
    uploaded: !updateError,
    error: updateError?.message ?? null,
  };
}
